/*
 * Kit tracking for bundle pages (product template suffix "bundles": the Fly
 * Tying Video Material Kits and the Fly Selections).
 *
 * A kit page adds its component products to the cart in one /cart/add.js call
 * (the "Add All to Cart" code in the template's Liquid block), so reports on
 * the kit product itself never see the sale. This script:
 *
 *   1. sends kit_view once per page load, after the component list renders;
 *   2. tags every line of a kit add with hidden line properties that carry
 *      through checkout onto the order (scripts/kit_report.py reads them):
 *        _kit_id      kit product id
 *        _kit_add     one id per successful kit add (groups the lines)
 *        _kit_role    "component" or "addon" (the fly box upsell)
 *        _kit_slot    component lines only: the kit's original variant id for
 *                     that row, so a swapped sold-out variant still counts
 *        _kit_slots   number of component rows the kit had at add time
 *        _kit_unavailable  rows that were sold out with no alternative chosen
 *        _kit_source  how the shopper first reached this kit this session
 *                     (left off when analytics consent is withheld)
 *      Unique _kit_add values also keep kit lines apart from the same variant
 *      bought on its own: Shopify only merges lines with identical properties.
 *   3. sends kit_add_to_cart once per successful kit add (never per component).
 *
 * It wraps window.fetch instead of editing the add-to-cart code because that
 * code lives in a customizer block in templates/product.bundles.json, which
 * the live theme owns. Only POSTs to /cart/add whose items all carry this
 * kit's "_bundle" property (or the old visible "Bundle") are touched;
 * everything else passes straight through.
 *
 * Analytics go to GA4 through window.gtag only (the Google & YouTube channel
 * app's tag), the same single channel as assets/book-a-call.js. Do not add a
 * second channel: every event would be counted twice.
 */
(function () {
  var configEl = document.getElementById('kit-tracking-config');
  if (!configEl || window.__tridentKitTracking) return;
  window.__tridentKitTracking = true;

  var config;
  try {
    config = JSON.parse(configEl.textContent);
  } catch (error) {
    return;
  }

  var KIT_ID = String(config.kitId);
  var VARIANT_GID = 'gid://shopify/ProductVariant/';
  var SOURCE_KEY = 'tf-kit-source:' + KIT_ID;

  /* ------------------------------------------------------------ consent --- */

  // The Customer Privacy API answers for the shopper's region and choice. When
  // it is not on the page, gtag's Consent Mode (set by the Google app) decides.
  function analyticsAllowed() {
    try {
      var privacy = window.Shopify && window.Shopify.customerPrivacy;
      if (privacy && typeof privacy.analyticsProcessingAllowed === 'function') {
        return privacy.analyticsProcessingAllowed() !== false;
      }
    } catch (error) {
      /* fall through */
    }
    return true;
  }

  /* ------------------------------------------------------------- source --- */

  function clip(value) {
    return String(value).slice(0, 100);
  }

  function describeReferrer() {
    var params = new URLSearchParams(window.location.search);
    if (params.get('utm_source')) return 'utm:' + params.get('utm_source').toLowerCase();

    var referrer;
    try {
      referrer = document.referrer ? new URL(document.referrer) : null;
    } catch (error) {
      referrer = null;
    }
    if (!referrer) return 'direct';
    if (referrer.hostname !== window.location.hostname) {
      return 'external:' + referrer.hostname.replace(/^www\./, '');
    }

    var path = referrer.pathname.replace(/\/+$/, '') || '/';
    var match;
    if (path === '/') return 'home';
    if ((match = path.match(/^\/collections\/([^/]+)\/products\//))) return 'collection:' + match[1];
    if ((match = path.match(/^\/collections\/([^/]+)/))) return 'collection:' + match[1];
    if ((match = path.match(/^\/products\/([^/]+)/))) return 'product:' + match[1];
    if ((match = path.match(/^\/blogs\/[^/]+\/([^/]+)/))) return 'article:' + match[1];
    if (path.indexOf('/blogs/') === 0) return 'blog';
    if (path.indexOf('/search') === 0) return 'search';
    if ((match = path.match(/^\/pages\/([^/]+)/))) return 'page:' + match[1];
    if (path.indexOf('/cart') === 0) return 'cart';
    return 'site:' + path;
  }

  // First source this session for this kit. Going back to the kit from one of
  // its component pages, or reloading, keeps the original source.
  var kitSource = (function () {
    try {
      var stored = sessionStorage.getItem(SOURCE_KEY);
      if (stored) return stored;
    } catch (error) {
      /* storage blocked: use this view's referrer */
    }
    var source = clip(describeReferrer());
    try {
      sessionStorage.setItem(SOURCE_KEY, source);
    } catch (error) {
      /* ignore */
    }
    return source;
  })();

  /* --------------------------------------------------------------- emit --- */

  // gtag arrives a couple of seconds into the page load; earlier events wait
  // in a queue (10s cap, then dropped).
  var queue = [];
  var flushTimer = null;

  function flush() {
    if (typeof window.gtag !== 'function') return false;
    while (queue.length) {
      var item = queue.shift();
      try {
        window.gtag('event', item.name, item.params);
      } catch (error) {
        if (item.params.event_callback) item.params.event_callback();
      }
    }
    return true;
  }

  // Resolves once gtag has sent the event (or after timeoutMs), so a page
  // navigation right after a kit add does not lose it.
  function emit(name, params, timeoutMs) {
    return new Promise(function (resolve) {
      if (!analyticsAllowed()) {
        resolve();
        return;
      }
      var done = false;
      function finish() {
        if (done) return;
        done = true;
        resolve();
      }
      params = Object.assign({}, params, { event_callback: finish, event_timeout: timeoutMs || 2000 });
      setTimeout(finish, (timeoutMs || 2000) + 200);
      queue.push({ name: name, params: params });
      try {
        // QA hook: listen for this to see what is sent without opening GA4.
        document.dispatchEvent(new CustomEvent('trident:kit-event', { detail: { name: name, params: params } }));
      } catch (error) {
        /* ignore */
      }
      if (flush() || flushTimer) return;
      var startedAt = Date.now();
      flushTimer = setInterval(function () {
        if (flush() || Date.now() - startedAt > 10000) {
          clearInterval(flushTimer);
          flushTimer = null;
          queue.length = 0;
        }
      }, 250);
    });
  }

  function baseParams() {
    return {
      kit_id: KIT_ID,
      kit_name: clip(config.kitName),
      kit_type: config.kitType,
      kit_source: kitSource
    };
  }

  /* --------------------------------------------------------------- page --- */

  function componentRows() {
    return document.querySelectorAll('#bundle-ui #bb-list .bb-row[data-variant]');
  }

  function rowForVariant(variantId) {
    var input = document.querySelector('#bundle-ui #bb-list input[data-gid="' + VARIANT_GID + variantId + '"]');
    return input ? input.closest('.bb-row[data-variant]') : null;
  }

  function isAddon(variantId) {
    return !!document.querySelector('#bundle-ui #bb-upsell-content [data-variant-id="' + VARIANT_GID + variantId + '"]');
  }

  function numericId(gid) {
    return String(gid || '').split('/').pop();
  }

  /* ----------------------------------------------------------- kit_view --- */

  var viewSent = false;

  function sendView(status) {
    if (viewSent) return;
    viewSent = true;
    var rows = componentRows();
    var available = 0;
    rows.forEach(function (row) {
      var input = row.querySelector('input[data-gid]');
      if (input && !input.disabled) available += 1;
    });
    emit('kit_view', Object.assign(baseParams(), {
      components_total: rows.length,
      components_available: available,
      load_status: status
    }));
  }

  function watchForComponents() {
    var list = document.querySelector('#bundle-ui #bb-list');
    if (!list) {
      sendView('no_list');
      return;
    }
    if (componentRows().length) {
      sendView('ok');
      return;
    }
    var observer = new MutationObserver(function () {
      if (componentRows().length || list.querySelector('.bb-err')) {
        observer.disconnect();
        sendView(componentRows().length ? 'ok' : 'error');
      }
    });
    observer.observe(list, { childList: true, subtree: true });
    // The list loads from the Storefront API; report the view even if it never does.
    setTimeout(function () {
      observer.disconnect();
      sendView(componentRows().length ? 'ok' : 'timeout');
    }, 8000);
  }

  /* ---------------------------------------------------- kit add-to-cart --- */

  function newAddId() {
    return Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
  }

  function requestUrl(input) {
    if (typeof input === 'string') return input;
    if (input && input.url) return input.url;
    return String(input || '');
  }

  // Returns the parsed body when this request is this kit's "Add All to Cart".
  function kitAddBody(input, init) {
    if (!init || typeof init.body !== 'string') return null;
    if (String(init.method || 'GET').toUpperCase() !== 'POST') return null;
    if (!/\/cart\/add(\.js)?(\?|$)/.test(requestUrl(input))) return null;
    var body;
    try {
      body = JSON.parse(init.body);
    } catch (error) {
      return null;
    }
    var items = body && body.items;
    if (!Array.isArray(items) || !items.length) return null;
    var ours = items.every(function (item) {
      return item && item.properties && (item.properties._bundle === config.kitName || item.properties.Bundle === config.kitName) && !item.properties._kit_add;
    });
    return ours ? body : null;
  }

  function decorate(body) {
    var addId = newAddId();
    var slots = componentRows().length;
    var unavailable = 0;
    componentRows().forEach(function (row) {
      var input = row.querySelector('input[data-gid]');
      if (!input || input.disabled) unavailable += 1;
    });
    var consented = analyticsAllowed();

    body.items.forEach(function (item) {
      var variantId = String(item.id);
      var row = rowForVariant(variantId);
      var role = row || !isAddon(variantId) ? 'component' : 'addon';
      var properties = Object.assign({}, item.properties, {
        _kit_id: KIT_ID,
        _kit_add: addId,
        _kit_role: role,
        _kit_slots: String(slots),
        _kit_unavailable: String(unavailable)
      });
      if (role === 'component') properties._kit_slot = row ? numericId(row.dataset.variant) : variantId;
      if (consented) properties._kit_source = kitSource;
      item.properties = properties;
    });

    return { addId: addId, slots: slots, unavailable: unavailable };
  }

  // Reports what actually landed in the cart under this add id. Shopify can
  // add some lines and still answer 422 ("Only 1 item was added to your cart
  // due to availability"), so the cart is the record, not the response.
  function reportAdd(add, status) {
    return nativeFetch.call(window, '/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        var lines = (cart.items || []).filter(function (item) {
          return item.properties && item.properties._kit_add === add.addId;
        });
        if (!lines.length) return null;

        var selectedSlots = {};
        var units = 0;
        var addonUnits = 0;
        var value = 0;
        var items = lines.map(function (line) {
          var role = line.properties._kit_role;
          var price = (Number(line.final_price) || 0) / 100;
          value += price * line.quantity;
          if (role === 'component') {
            units += line.quantity;
            selectedSlots[line.properties._kit_slot] = true;
          } else {
            addonUnits += line.quantity;
          }
          return {
            item_id: String(line.variant_id),
            item_name: line.product_title || '',
            item_variant: line.variant_title || '',
            item_category: role === 'component' ? 'kit component' : 'kit add-on',
            item_list_id: KIT_ID,
            item_list_name: config.kitName,
            price: price,
            quantity: line.quantity
          };
        });
        var selected = Object.keys(selectedSlots).length;

        return emit('kit_add_to_cart', Object.assign(baseParams(), {
          kit_add_id: add.addId,
          add_status: status,
          components_total: add.slots,
          components_selected: selected,
          components_unavailable: add.unavailable,
          kit_complete: selected >= add.slots ? 'yes' : 'no',
          units: units,
          addon_units: addonUnits,
          value: Math.round(value * 100) / 100,
          currency: config.currency,
          items: items
        }), 1500);
      })
      .catch(function () {
        /* analytics must never break the add */
      });
  }

  var nativeFetch = window.fetch;
  if (typeof nativeFetch === 'function') {
    window.fetch = function (input, init) {
      var body = kitAddBody(input, init);
      if (!body) return nativeFetch.apply(this, arguments);

      var add;
      try {
        add = decorate(body);
        init = Object.assign({}, init, { body: JSON.stringify(body) });
      } catch (error) {
        return nativeFetch.apply(this, arguments);
      }

      return nativeFetch.call(this, input, init).then(function (response) {
        if (!response.ok && response.status !== 422) return response;
        // The page redirects to /cart as soon as this resolves, so hold the
        // response until gtag has sent the event (about 1.5s cap).
        return reportAdd(add, response.ok ? 'ok' : 'stock_limited').then(function () {
          return response;
        });
      });
    };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchForComponents, { once: true });
  } else {
    watchForComponents();
  }
})();
